import { useState, useEffect, useRef, useCallback } from 'react';
import useTabParam from '../hooks/useTabParam';
import {
  Upload, Server, GitCommit, Database, Clock, CheckCircle, XCircle,
  AlertTriangle, Loader, Terminal, RotateCcw, ArrowRight, RefreshCw,
} from 'lucide-react';

function StatusDot({ status }) {
  const colors = { online: '#10B981', offline: '#EF4444', deploying: '#F59E0B', unknown: '#9CA3AF' };
  return (
    <span style={{
      display: 'inline-block', width: 8, height: 8, borderRadius: 4,
      background: colors[status] || colors.unknown,
      boxShadow: status === 'online' ? `0 0 6px ${colors.online}60` : 'none',
    }} />
  );
}

function parseLogLine(line) {
  if (line.startsWith('{')) {
    try {
      const parsed = JSON.parse(line);
      return parsed.message || '';
    } catch { return line; }
  }
  return line.replace(/\x1b\[[0-9;]*m|\033\[[0-9;]*m|\[0[;]?[0-9]*m/g, '');
}

export default function DeploymentPage() {
  const [tab, setTab] = useTabParam('overview');
  const [status, setStatus] = useState(null);
  const [checks, setChecks] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deploying, setDeploying] = useState(false);
  const [deployLog, setDeployLog] = useState([]);
  const [deployFinished, setDeployFinished] = useState(false);
  const [deploySuccess, setDeploySuccess] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showRollbackConfirm, setShowRollbackConfirm] = useState(null);
  // #1734: Dry-Run→Review→Confirm + Ein-Klick-Rollback
  const [review, setReview] = useState(null);
  const [confirmError, setConfirmError] = useState(null);
  const [releasesInfo, setReleasesInfo] = useState(null);
  const [showReleaseRollback, setShowReleaseRollback] = useState(false);
  const logRef = useRef(null);
  const pollRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const res = await fetch('/api/deployment/status', { credentials: 'include' });
      if (res.ok) setStatus(await res.json());
    } catch {}
  }, []);

  const loadChecks = useCallback(async () => {
    try {
      const res = await fetch('/api/deployment/checks', { credentials: 'include' });
      if (res.ok) setChecks(await res.json());
    } catch {}
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const res = await fetch('/api/deployment/history', { credentials: 'include' });
      if (res.ok) setHistory(await res.json());
    } catch {}
  }, []);

  const loadReleases = useCallback(async () => {
    try {
      const res = await fetch('/api/deployment/releases', { credentials: 'include' });
      if (res.ok) setReleasesInfo(await res.json());
    } catch {}
  }, []);

  useEffect(() => {
    Promise.all([loadStatus(), loadChecks(), loadHistory(), loadReleases()]).then(() => setLoading(false));
  }, [loadStatus, loadChecks, loadHistory, loadReleases]);

  function pollLog(onFinished) {
    let linesSeen = 0;
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`/api/deployment/log?since=${linesSeen}`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (data.lines && data.lines.length > 0) {
            setDeployLog(prev => [...prev, ...data.lines]);
            linesSeen = data.total;
          }
          if (data.finished) {
            clearInterval(pollRef.current);
            setDeploying(false);
            setDeployFinished(true);
            setDeploySuccess(data.success);
            loadStatus();
            loadHistory();
            if (onFinished) onFinished(data);
          }
        }
      } catch {}
    }, 1000);
  }

  function resetLogState() {
    setDeploying(true);
    setDeployLog([]);
    setDeployFinished(false);
    setDeploySuccess(false);
    setTab('deploy');
  }

  // #1734: "Deploy to Live" startet IMMER zuerst einen Dry-Run (read-only).
  async function startDryRun() {
    setReview(null);
    setConfirmError(null);
    resetLogState();

    await fetch('/api/deployment/deploy', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dryRun: true }),
    });

    pollLog(async (data) => {
      if (data.dryRun) {
        try {
          const r = await fetch('/api/deployment/dryrun-report', { credentials: 'include' });
          if (r.ok) { setReview(await r.json()); setTab('overview'); }
        } catch {}
      }
    });
  }

  // #1734: Real-Deploy NUR nach explizitem Confirm mit exakt dem reviewten Commit.
  async function confirmDeploy() {
    setShowConfirm(false);
    if (!review?.report?.commit) return;
    setConfirmError(null);
    resetLogState();

    const res = await fetch('/api/deployment/deploy', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirmed: true, reviewedCommit: review.report.commit }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      setConfirmError(err.error || `Bestätigung abgelehnt (HTTP ${res.status})`);
      setDeploying(false);
      setTab('overview');
      return;
    }
    setReview(null);
    pollLog();
  }

  // #1734: Ein-Klick-Rollback aufs vorherige Release
  async function startReleaseRollback() {
    setShowReleaseRollback(false);
    resetLogState();
    await fetch('/api/deployment/rollback-release', { method: 'POST', credentials: 'include' });
    pollLog(() => loadReleases());
  }

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [deployLog]);

  async function startRollback(timestamp) {
    setShowRollbackConfirm(null);
    await fetch('/api/deployment/rollback', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ timestamp }),
    });
    setTimeout(() => { loadStatus(); loadHistory(); }, 5000);
  }

  const tabs = [
    { id: 'overview', label: 'Übersicht', icon: Server },
    { id: 'commits', label: `Commits${status?.newCommits?.length ? ` (${status.newCommits.length})` : ''}`, icon: GitCommit },
    { id: 'migrations', label: `Migrationen${status?.migrations?.length ? ` (${status.migrations.length})` : ''}`, icon: Database },
    { id: 'deploy', label: 'Deploy', icon: Upload },
    { id: 'history', label: 'History', icon: Clock },
  ];

  if (loading) return (
    <div className="flex items-center justify-center h-96">
      <Loader size={24} className="animate-spin text-[var(--text-tertiary)]" />
    </div>
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-[10px] bg-gradient-to-br from-[#338edc] to-[#6366F1] flex items-center justify-center">
            <Upload size={18} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[var(--text-primary)]">Deployment</h1>
            <p className="text-xs text-[var(--text-secondary)]">Dev → Live Pipeline</p>
          </div>
        </div>

        <div className="flex gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-amber-200 dark:border-amber-800" style={{ background: 'var(--bg-card)' }}>
            <StatusDot status={status?.dev?.status || 'unknown'} />
            <span className="text-[11px] font-semibold text-amber-600 dark:text-amber-300">DEV</span>
            <span className="text-[10px] text-[var(--text-tertiary)]">{status?.dev?.commit || '—'}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-green-200 dark:border-green-800" style={{ background: 'var(--bg-card)' }}>
            <StatusDot status={status?.live?.status || 'unknown'} />
            <span className="text-[11px] font-semibold text-green-600 dark:text-green-300">LIVE</span>
            <span className="text-[10px] text-[var(--text-tertiary)]">{status?.live?.health?.version || '—'}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4">
        {tabs.map(tb => {
          const Icon = tb.icon;
          return (
            <button key={tb.id} onClick={() => setTab(tb.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-all ${
                tab === tb.id
                  ? 'bg-[#338edc] text-white font-semibold'
                  : 'text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover)]'
              }`}>
              <Icon size={13} /> {tb.label}
            </button>
          );
        })}
      </div>

      {/* TAB: OVERVIEW */}
      {tab === 'overview' && (
        <div>
          <div className="grid grid-cols-[1fr_60px_1fr] gap-4 mb-4">
            {/* Dev */}
            <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Server size={16} className="text-amber-500" />
                <span className="text-sm font-bold text-[var(--text-primary)]">Development</span>
                <StatusDot status={status?.dev?.status} />
              </div>
              <div className="text-xs text-[var(--text-secondary)] space-y-1.5">
                <div>URL: <code className="text-[10px] bg-[var(--bg-inset)] px-1.5 py-0.5 rounded">dev.app.weship.eu</code></div>
                <div>Commit: <strong className="text-[var(--text-primary)]">{status?.dev?.commit}</strong></div>
                <div>Branch: <strong className="text-[var(--text-primary)]">{status?.dev?.branch}</strong></div>
                <div>Git: {status?.dev?.clean
                  ? <span className="text-green-600 font-medium">Clean</span>
                  : <span className="text-red-500 font-medium">{status?.dev?.uncommittedFiles} uncommitted</span>}
                </div>
                <div>GitHub: {!status?.remote?.configured
                  ? <span className="text-red-500 font-medium">kein Remote</span>
                  : !status?.remote?.reachable
                    ? <span className="text-red-500 font-medium">nicht erreichbar</span>
                    : status?.remote?.inSync
                      ? <span className="text-green-600 font-medium">gesichert ({status.remote.remote}/{status.remote.branch})</span>
                      : <span className="text-amber-600 font-medium">{status?.remote?.unpushed ?? '?'} Commit(s) ungepusht</span>}
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center justify-center">
              <div className="w-10 h-10 rounded-xl border-2 border-[#338edc] flex items-center justify-center" style={{ background: 'var(--bg-card)' }}>
                <ArrowRight size={18} className="text-[#338edc]" />
              </div>
              <span className="text-[10px] text-[#338edc] font-semibold mt-1">
                {status?.newCommits?.length || 0}
              </span>
            </div>

            {/* Live */}
            <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Server size={16} className="text-green-500" />
                <span className="text-sm font-bold text-[var(--text-primary)]">Production</span>
                <StatusDot status={status?.live?.status} />
              </div>
              <div className="text-xs text-[var(--text-secondary)] space-y-1.5">
                <div>URL: <code className="text-[10px] bg-[var(--bg-inset)] px-1.5 py-0.5 rounded">app.weship.eu</code></div>
                <div>Version: <strong className="text-[var(--text-primary)]">{status?.live?.health?.version || '—'}</strong></div>
                <div>API: {status?.live?.health
                  ? <span className="text-green-600 font-medium">OK</span>
                  : <span className="text-[var(--text-tertiary)]">—</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Pre-Deploy Checks */}
          <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-5 mb-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-bold text-[var(--text-primary)]">Pre-Deploy Checks</span>
              <button onClick={loadChecks} className="text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] transition-colors">
                <RefreshCw size={13} />
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {checks.map((c, i) => (
                <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg border border-[var(--border-table)]">
                  {c.status === 'ok' ? <CheckCircle size={14} className="text-green-500 flex-shrink-0" /> :
                   c.status === 'warn' ? <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" /> :
                   <XCircle size={14} className="text-red-500 flex-shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-[11px] font-semibold text-[var(--text-primary)]">{c.name}</div>
                    <div className="text-[10px] text-[var(--text-tertiary)] truncate">{c.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Deploy Button — #1734: startet IMMER zuerst einen Dry-Run (Review→Confirm) */}
          <button onClick={startDryRun}
            disabled={deploying || !status?.canDeploy}
            className="w-full py-3.5 rounded-xl text-white text-sm font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: deploying || !status?.canDeploy ? '#9CA3AF' : 'linear-gradient(135deg, #338edc, #6366F1)' }}>
            <Upload size={18} /> Deploy to Live
          </button>
          <p className="text-[11px] text-[var(--text-tertiary)] text-center mt-1.5">
            Schritt 1: Dry-Run (read-only, alle Gates) → Schritt 2: Review → Schritt 3: Bestätigen. Ohne Bestätigung passiert KEIN Real-Deploy.
          </p>
          {!status?.canDeploy && status?.dev && !status?.dev?.clean && (
            <p className="text-xs text-red-500 text-center mt-2">Uncommitted Changes auf Dev — bitte erst committen</p>
          )}

          {confirmError && (
            <div className="mt-3 p-3 rounded-lg border border-red-200 text-xs text-red-600 dark:border-red-800 dark:text-red-300" style={{ background: 'var(--bg-inset)' }}>
              <strong>Deploy abgelehnt:</strong> {confirmError}
            </div>
          )}

          {/* #1734 Review-Panel (Dry-Run-Ergebnis → Bestätigen) */}
          {review?.report && (
            <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-5 mt-4">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <span className="text-sm font-bold text-[var(--text-primary)]">Dry-Run-Review</span>
                  <span className="text-xs text-[var(--text-secondary)] ml-2">
                    Commit <code className="text-[#338edc] font-semibold">{review.report.commit}</code>
                    {' '}· vor {review.ageMinutes} min
                  </span>
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${
                  review.confirmable
                    ? 'border-green-200 text-green-600 dark:border-green-800 dark:text-green-300'
                    : 'border-red-200 text-red-500 dark:border-red-800 dark:text-red-300'
                }`}>
                  {review.confirmable ? 'Bestätigbar' : (review.matchesHead ? 'Report zu alt — Dry-Run neu laufen lassen' : `HEAD ist ${review.headCommit} — Dry-Run neu laufen lassen`)}
                </span>
              </div>

              {/* Gates */}
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
                {Object.entries(review.report.gates || {}).map(([name, st]) => (
                  <div key={name} className="flex items-center gap-1.5 p-2 rounded-lg border border-[var(--border-table)]">
                    {st === 'ok' ? <CheckCircle size={12} className="text-green-500 flex-shrink-0" /> :
                     <AlertTriangle size={12} className="text-amber-500 flex-shrink-0" />}
                    <span className="text-[10px] text-[var(--text-primary)] font-medium">{name}</span>
                    <span className="text-[9px] text-[var(--text-tertiary)] ml-auto">{st}</span>
                  </div>
                ))}
              </div>

              <div className="text-xs text-[var(--text-secondary)] space-y-2">
                {/* Route-Diff */}
                <div>
                  <strong className="text-[var(--text-primary)]">Routen:</strong> {review.report.routeDiff?.total} im Build,
                  {' '}<span className="text-green-600 font-medium">+{review.report.routeDiff?.added?.length || 0} neu</span>,
                  {' '}<span className={review.report.routeDiff?.removed?.length ? 'text-red-500 font-medium' : ''}>−{review.report.routeDiff?.removed?.length || 0} entfernt</span>
                  {(review.report.routeDiff?.added?.length > 0 || review.report.routeDiff?.removed?.length > 0) && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-[11px] text-[var(--text-tertiary)]">Details</summary>
                      {(review.report.routeDiff.added || []).map((r, i) => <div key={`a${i}`} className="text-green-600 text-[11px] font-mono">+ {r}</div>)}
                      {(review.report.routeDiff.removed || []).map((r, i) => <div key={`r${i}`} className="text-red-500 text-[11px] font-mono">− {r}</div>)}
                    </details>
                  )}
                </div>

                {/* rsync */}
                <div>
                  <strong className="text-[var(--text-primary)]">rsync:</strong> {review.report.rsync?.count} Datei(en) würden übertragen (ohne --delete)
                  <details className="mt-1">
                    <summary className="cursor-pointer text-[11px] text-[var(--text-tertiary)]">Dateiliste{review.report.rsync?.truncated ? ' (erste 500)' : ''}</summary>
                    <div className="max-h-48 overflow-y-auto mt-1 p-2 rounded-lg font-mono text-[10px]" style={{ background: 'var(--bg-inset)' }}>
                      {(review.report.rsync?.files || []).map((f, i) => <div key={i}>{f}</div>)}
                    </div>
                  </details>
                </div>

                {/* Migrationen */}
                <div>
                  <strong className="text-[var(--text-primary)]">Migrationen:</strong> {review.report.migrations?.count || 0}
                  {review.report.migrations?.sql && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-[11px] text-[var(--text-tertiary)]">SQL anzeigen</summary>
                      <code className="block text-[10px] p-2 rounded-lg font-mono overflow-x-auto whitespace-pre-wrap mt-1" style={{ background: '#1A1A2E', color: '#E8EAF2' }}>
                        {review.report.migrations.sql}
                      </code>
                    </details>
                  )}
                  {' '}· <strong className="text-[var(--text-primary)]">Entfernungen:</strong> {review.report.removals?.length || 0}
                  {(review.report.removals || []).map((r, i) => <div key={i} className="text-amber-600 text-[11px] font-mono">→ {r}</div>)}
                </div>

                {/* ENV-Guard + Health-Gate */}
                <div>
                  <strong className="text-[var(--text-primary)]">ENV-Guard:</strong> {(review.report.envGuard?.vars || []).join(', ')} — {review.report.envGuard?.status} ({review.report.envGuard?.note})
                </div>
                <div>
                  <strong className="text-[var(--text-primary)]">Frontend:</strong> {review.report.frontend?.distFiles} Dateien → {review.report.frontend?.targetRelease} (aktuell LIVE: {review.report.frontend?.currentLiveRelease ? review.report.frontend.currentLiveRelease.split('/').pop() : '—'})
                </div>
                <div>
                  <strong className="text-[var(--text-primary)]">Health-Gate:</strong> {review.report.healthGate?.attempts}× à {review.report.healthGate?.intervalSeconds}s auf {review.report.healthGate?.url} · bei Fail: {review.report.healthGate?.autoRollback}
                </div>

                {/* GitHub-Push-Gate (#1825) */}
                <div>
                  <strong className="text-[var(--text-primary)]">GitHub-Push:</strong>{' '}
                  {review.report.push?.state === 'skipped'
                    ? <span className="text-amber-600 font-medium">Notausgang aktiv — {review.report.push?.skipReason || 'ohne Begründung'}</span>
                    : <>vor dem rsync nach {review.report.push?.remote}/{review.report.push?.branch} ({review.report.push?.commits ?? 0} Commit(s)) · Geheimnis-Scan: {review.report.gates?.secretScan}</>}
                  <div className="text-[11px] text-[var(--text-tertiary)] mt-0.5">
                    Remote-HEAD: <span className="font-mono">{review.report.push?.remoteHead ? review.report.push.remoteHead.slice(0, 8) : '—'}</span>
                    {' '}· schlägt der Push fehl, gibt es keinen rsync.
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => { setReview(null); setConfirmError(null); }}
                  className="px-4 py-2 text-sm rounded-[10px] border border-[var(--border-card)] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover)] transition-colors">
                  Verwerfen
                </button>
                <button onClick={() => setShowConfirm(true)}
                  disabled={!review.confirmable || deploying}
                  className="px-5 py-2 text-sm font-semibold rounded-[10px] bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                  Bestätigen &amp; Deployen
                </button>
              </div>
            </div>
          )}

          {/* #1734 Ein-Klick-Rollback (vorheriges Release) */}
          <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-5 mt-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-xs text-[var(--text-secondary)]">
                <div className="text-sm font-bold text-[var(--text-primary)] mb-1">Frontend-Release (LIVE)</div>
                {releasesInfo?.isSymlink ? (
                  <>
                    <div>Aktuell: <strong className="text-[var(--text-primary)]">{releasesInfo.current || '—'}</strong></div>
                    <div>Vorheriges: <strong className="text-[var(--text-primary)]">{releasesInfo.previous || '— (kein Rollback möglich)'}</strong></div>
                  </>
                ) : (
                  <div>dist ist noch kein Symlink (Einmal-Migration ausstehend) — Rollback nicht verfügbar.</div>
                )}
              </div>
              <button onClick={() => setShowReleaseRollback(true)}
                disabled={deploying || !releasesInfo?.canRollback}
                className="flex items-center gap-1.5 px-3 py-2 rounded-[10px] text-xs font-semibold border border-amber-200 text-amber-800 hover:bg-amber-50 dark:border-amber-800 dark:text-amber-200 dark:hover:bg-amber-900/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                <RotateCcw size={13} /> Rollback aufs vorherige Release
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TAB: COMMITS */}
      {tab === 'commits' && (
        <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-5">
          <div className="text-sm font-bold text-[var(--text-primary)] mb-1">Neue Commits seit letztem Deploy</div>
          <div className="text-xs text-[var(--text-secondary)] mb-4">{status?.newCommits?.length || 0} Commits</div>
          {(status?.newCommits || []).map((c, i) => (
            <div key={i} className="flex items-center gap-3 py-2.5 border-b border-[var(--border-table)] last:border-0">
              <code className="text-[11px] text-[#338edc] px-2 py-0.5 rounded font-semibold flex-shrink-0" style={{ background: 'var(--bg-inset)' }}>
                {c.hash}
              </code>
              <span className="text-sm text-[var(--text-primary)] flex-1">{c.message}</span>
            </div>
          ))}
          {status?.pendingUnknown && (
            <div className="flex items-center gap-2 py-3 text-sm text-amber-600">
              <AlertTriangle size={15} className="flex-shrink-0" />
              <span>Pending unbekannt — {status?.pendingReason || 'Deploy-History nicht lesbar'}</span>
            </div>
          )}
          {!status?.pendingUnknown && (!status?.newCommits || status.newCommits.length === 0) && (
            <div className="text-center py-8 text-[var(--text-tertiary)] text-sm">Keine neuen Commits</div>
          )}
        </div>
      )}

      {/* TAB: MIGRATIONS */}
      {tab === 'migrations' && (
        <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4">
            {status?.migrations?.length > 0 && <AlertTriangle size={16} className="text-amber-500" />}
            <span className="text-sm font-bold text-[var(--text-primary)]">
              {status?.migrations?.length || 0} DB-Migrationen
            </span>
          </div>
          {(status?.migrations || []).map((m, i) => (
            <div key={i} className="mb-1.5">
              <code className="block text-[11px] p-2.5 rounded-lg font-mono overflow-x-auto" style={{ background: '#1A1A2E', color: '#E8EAF2' }}>
                {m}
              </code>
            </div>
          ))}
          {(!status?.migrations || status.migrations.length === 0) && (
            <div className="text-center py-8 text-[var(--text-tertiary)] text-sm">Keine ausstehenden Migrationen</div>
          )}
        </div>
      )}

      {/* TAB: DEPLOY (Live Log) */}
      {tab === 'deploy' && (
        <div className="rounded-2xl overflow-hidden border border-[#2E3148]" style={{ background: '#0F1117' }}>
          <div className="flex items-center gap-2 px-4 py-2 border-b border-[#2E3148]">
            <Terminal size={12} className="text-[var(--text-secondary,#4B5563)]" />
            <span className="text-[11px] text-[var(--text-secondary,#4B5563)]">Deployment Log</span>
            {deploying && <Loader size={10} className="animate-spin text-[#338edc] ml-auto" />}
            {deployFinished && (
              <span className={`text-[10px] font-semibold ml-auto px-2 py-0.5 rounded ${
                deploySuccess ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'
              }`}>
                {deploySuccess ? 'Erfolgreich' : 'Fehlgeschlagen'}
              </span>
            )}
          </div>
          <div ref={logRef} className="p-4 max-h-[400px] overflow-y-auto font-mono text-xs leading-relaxed">
            {deployLog.length === 0 ? (
              <span className="text-[var(--text-secondary,#4B5563)]">
                {deploying ? 'Deployment wird gestartet...' : 'Warte auf Deployment-Start. Klicke "Deploy to Live" in der Übersicht.'}
              </span>
            ) : deployLog.map(parseLogLine).filter(l => l.trim()).map((line, i) => (
              <div key={i} className={
                line.includes('✓') || line.includes('ERFOLGREICH') || line.includes('success') ? 'text-green-400' :
                line.includes('✗') || line.includes('ERROR') || line.includes('ABGEBROCHEN') || line.includes('failed') ? 'text-red-400' :
                line.includes('⚠') || line.includes('warn') ? 'text-amber-400' :
                line.includes('━━━') || line.includes('═══') ? 'text-blue-400' :
                'text-[var(--text-tertiary,#6B7280)]'
              }>{line}</div>
            ))}
            {deploying && <span className="text-[#338edc] animate-pulse">|</span>}
          </div>
        </div>
      )}

      {/* TAB: HISTORY */}
      {tab === 'history' && (
        <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-5">
          <div className="text-sm font-bold text-[var(--text-primary)] mb-4">Deployment History</div>
          {history.map((h, i) => (
            <div key={i} className="flex items-center gap-3 py-3 border-b border-[var(--border-table)] last:border-0">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 border ${
                h.status === 'success' ? 'border-green-200 dark:border-green-800' :
                h.status === 'rolled_back' ? 'border-amber-200 dark:border-amber-800' :
                'border-red-200 dark:border-red-800'
              }`}>
                {h.status === 'success' ? <CheckCircle size={14} className="text-green-500" /> :
                 h.status === 'rolled_back' ? <RotateCcw size={14} className="text-amber-500" /> :
                 <XCircle size={14} className="text-red-500" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-[var(--text-primary)]">#{h.id?.substring(0, 8)}</span>
                  <span className="text-xs text-[#338edc] font-semibold">{h.dev_commit}</span>
                  <span className="text-[10px] text-[var(--text-tertiary)]">{h.timestamp ? new Date(h.timestamp).toLocaleString('de-AT') : '—'}</span>
                </div>
                <div className="text-[11px] text-[var(--text-secondary)]">
                  {/* #1735 — migrations ist Zahl (Deploy-Record) ODER {count, sql} (Dry-Run-Report) → nie Objekt als Child rendern (React #31) */}
                  {(() => {
                    const mCount = (h.migrations && typeof h.migrations === 'object') ? (h.migrations.count ?? 0) : (h.migrations ?? 0);
                    return <>{mCount} Migration{mCount !== 1 ? 'en' : ''}</>;
                  })()}
                  {h.status === 'rolled_back' && <span className="text-amber-500 font-medium"> · Zurückgerollt</span>}
                  {/* #1825: „Liegt der ausgelieferte Stand auf GitHub?" ohne Nachforschung beantwortbar */}
                  {h.push?.state === 'pushed' && (
                    <span className="text-green-600 font-medium">
                      {' '}· GitHub {h.push.remote}/{h.push.branch} <span className="font-mono">{String(h.push.head).slice(0, 8)}</span> verifiziert
                    </span>
                  )}
                  {h.push?.state === 'skipped' && (
                    <span className="text-amber-600 font-medium"> · Push übersprungen: {h.push.skip_reason || 'ohne Begründung'}</span>
                  )}
                </div>
              </div>
              {i === 0 && h.status === 'success' && (
                <button onClick={() => setShowRollbackConfirm(h.id)}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] border border-amber-200 text-amber-800 hover:bg-amber-50 dark:border-amber-800 dark:text-amber-200 dark:hover:bg-amber-900/20 transition-colors">
                  <RotateCcw size={11} /> Rollback
                </button>
              )}
            </div>
          ))}
          {history.length === 0 && (
            <div className="text-center py-8 text-[var(--text-tertiary)] text-sm">Noch keine Deployments</div>
          )}
        </div>
      )}

      {/* CONFIRM DEPLOY MODAL — #1734: finale Bestätigung des reviewten Dry-Runs */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-[var(--bg-card)] rounded-2xl p-6 max-w-md w-full mx-4 border border-[var(--border-card)]" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
            <h3 className="text-base font-bold text-[var(--text-primary)] mb-2">Real-Deploy bestätigen?</h3>
            <p className="text-sm text-[var(--text-secondary)] mb-4 leading-relaxed">
              Deployt exakt den reviewten Commit <code className="text-[#338edc] font-semibold">{review?.report?.commit}</code> auf <strong>app.weship.eu</strong>
              {' '}({review?.report?.rsync?.count} Dateien, {review?.report?.migrations?.count || 0} Migrationen,
              {' '}Routen +{review?.report?.routeDiff?.added?.length || 0}/−{review?.report?.routeDiff?.removed?.length || 0}).
            </p>
            <div className="p-3 rounded-lg border border-amber-200 text-xs text-amber-800 mb-4 dark:border-amber-800 dark:text-amber-200" style={{ background: 'var(--bg-inset)' }}>
              Kurzer Restart (~10s). Health-Gate pollt danach auf HTTP 200 — bei Fail: automatischer Frontend-Rollback.
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowConfirm(false)}
                className="px-4 py-2 text-sm rounded-[10px] border border-[var(--border-card)] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover)] transition-colors">
                Abbrechen
              </button>
              <button onClick={confirmDeploy}
                className="px-5 py-2 text-sm font-semibold rounded-[10px] bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors">
                Jetzt wirklich deployen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RELEASE-ROLLBACK MODAL (#1734) */}
      {showReleaseRollback && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-[var(--bg-card)] rounded-2xl p-6 max-w-sm w-full mx-4 border border-[var(--border-card)]" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
            <h3 className="text-base font-bold text-[var(--text-primary)] mb-2">Rollback aufs vorherige Release?</h3>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              Frontend-Symlink: <strong className="text-[var(--text-primary)]">{releasesInfo?.current}</strong> → <strong className="text-[var(--text-primary)]">{releasesInfo?.previous}</strong>
              {' '}+ pm2 restart. Kein Build, kein rsync — Backend-Code und DB bleiben unverändert.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowReleaseRollback(false)}
                className="px-4 py-2 text-sm rounded-[10px] border border-[var(--border-card)] text-[var(--text-secondary)]">
                Abbrechen
              </button>
              <button onClick={startReleaseRollback}
                className="px-5 py-2 text-sm font-semibold rounded-[10px] bg-amber-500 text-white hover:bg-amber-600 transition-colors">
                Rollback starten
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ROLLBACK MODAL */}
      {showRollbackConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-[var(--bg-card)] rounded-2xl p-6 max-w-sm w-full mx-4 border border-[var(--border-card)]" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
            <h3 className="text-base font-bold text-[var(--text-primary)] mb-2">Rollback?</h3>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              Das Live-System wird auf den vorherigen Stand zurückgesetzt. Die DB wird aus dem Backup wiederhergestellt.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowRollbackConfirm(null)}
                className="px-4 py-2 text-sm rounded-[10px] border border-[var(--border-card)] text-[var(--text-secondary)]">
                Abbrechen
              </button>
              <button onClick={() => startRollback(showRollbackConfirm)}
                className="px-5 py-2 text-sm font-semibold rounded-[10px] bg-red-500 text-white hover:bg-red-600 transition-colors">
                Rollback starten
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
